/* eslint-disable */

/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path ui
 * @description Navigation main menu
 * @section 4.5
 *
 */
import { Typography } from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import clsx from "clsx";
import { getElementName } from "qpr-base/tplutils/utils";
import React, { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Data } from "./../apihooks/useNavitems";
import { useAppContext } from "./../context/AppCtxProvider";
import { checkIfExternal, getFileApiURL } from "./../utils/url-utils";
import { makeThemedStyles } from "./../viewcomponents/utils";
import VerticalScroll from "./VerticalScroll";
interface MenuColumnProps {
  data?: Data;
  level: number;
  tenant: string;
  values: Array<string | boolean>;
  handleChange: (newValue: string | boolean, level: number) => void;
  toggleMenu: () => void;
  onSelect?: (item: Data) => void;
  RenderTab?: React.FC<{
    item: Data;
    onClick: () => void;
  }>;
}
export const useStyles = makeThemedStyles(
  "HorizontalMenu.MenuColumn",
  (theme) => ({
    menuColumn: {
      paddingLeft: theme.spacing(6),
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.palette.background.paper,
      height: "100%",
      "& $button": {
        visibility: "hidden",
      },
      [theme.breakpoints.down("sm")]: {
        "& $button": {
          visibility: "hidden",
        },
        "&$menuColumn:not(:first-of-type)": {
          "& $button": {
            visibility: "visible",
          },
          position: "absolute",
          left: "0px",
        },
      },
    },
    button: {},
    selectedTab: {
      borderRight: "1px solid rgb(0, 169, 163)",
      "& *": {
        color: "rgb(0, 169, 163) !important",
      },
    },
    column_header: {
      padding: `0 ${theme.spacing(2)}px`,
      color: "rgb(17, 94, 103)",
      margin: "1.64em 0 0.071em 0",
      fontSize: "1.6em !important",
      lineHeight: "2em",
      letterSpacing: "0.05px",
      textAlign: "left",
    },
    navItem: {
      fontSize: "0.8em !important",
      letterSpacing: "0.05px",
      margin: 0,
      width: "100%",
      textAlign: "left",
    },
    divider: {
      float: "left",
      marginLeft: "-1px",
      zIndex: 1,
    },
    tabRoot: {
      textTransform: "none",
      padding: 0,
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      maxWidth: "1000px",
    },
    tab: {
      paddingTop: "0.55em",
      paddingBottom: "0.55em",
      "&.not-visited": {
        "& h6": {
          color: "rgb(17, 94, 103) !important",
        },
      },
      "& h6": {
        color: "rgb(109, 132, 136)",
      },
    },
    activeTab: {
      borderRight: "1px solid rgb(0, 169, 163)",
      "& h6": {
        color: "rgb(0, 169, 163) !important",
      },
    },
    childNavItem: {
      "& h6": {
        fontSize: "0.65em !important",
        color: "rgb(109, 132, 136)",
      },
    },
    wrapper: {
      flexDirection: "row-reverse",
      justifyContent: "start",
    },
    labelIcon: {
      minHeight: "0px",
    },
    icon: {
      marginRight: "1.065em",
      marginLeft: "0.66em",
    },
    iconImg: {
      height: "1.6em",
      maxWidth: "1.6em",
      order: 2,
      marginBottom: "0px!important",
    },
    tabsRoot: {},
    selectedIndicator: { display: "none" },
    scrollContainer: {
      flex: "initial",
      maxHeight: "453px",
    },
    contentContainer: {
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none !important",
      },
    },
    missingImage: {
      display: "none",
    },
    column: {
      minWidth: "calc(100% / 6)",
      maxWidth: "calc(100% / 5)",
      animation: `$fadeIn 1s ${theme.transitions.easing.easeInOut}`,
    },
    "@keyframes fadeIn": {
      "0%": {
        opacity: 0,
      },
      "100%": {
        opacity: 1,
      },
    },
  })
);
// move the get icon from the render loop
export const getIcon = (item: Data) => {
  const [hasError, setError] = React.useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  // in case no fallback and error render DIV with classname "missingImage" which can be themed
  if (hasError || typeof item.icon === "undefined") {
    return <div className={classes.missingImage} />;
  }
  if (typeof item.icon === "undefined") {
    return <div className={classes.missingImage} />;
  }
  return typeof item.icon === "string" ? (
    <div
      style={{
        height: "2em",
        width: "2em",
        marginRight: "1.065em",
        marginLeft: "0.66em",
        background: "rgb(0, 169, 163)",
        WebkitMask: `url(${getFileApiURL(
          `${item.icon}`
        )}) no-repeat center / contain`,
      }}
      className={clsx(classes.iconImg)}
    />
  ) : item.icon ? (
    item.icon
  ) : (
    <div className={classes.missingImage} />
  );
};
const MenuColumn = ({
  data,
  level,
  tenant,
  toggleMenu,
  handleChange,
  RenderTab,
  values = [],
}: MenuColumnProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { language, rtl, model } = useAppContext();
  const navRef = useRef<HTMLSpanElement[]>([]);
  const dividerRef = useRef<HTMLHRElement>(null);
  const mouseThrottleTimer = useRef<NodeJS.Timeout>();
  interface IData {
    [key: string]: any;
  }
  const temp: IData = {};
  if (!data) return null;
  const getTitle = (item: Data) => {
    if (item.customValue1) {
      return getElementName(
        { ...item, ...{ JS_ATTR: item.customValue1 } },
        language
      );
    }
    return t(`navItems.${item.title}`, item.title);
  };
  const groupedData = data.children.reduce((obj, item) => {
    // If the length doesn't already exist as a key in the object, create it
    // eslint-disable-next-line no-prototype-builtins
    if (item.group && !obj.hasOwnProperty(item.group)) {
      obj[item.group as string] = [];
      // eslint-disable-next-line no-prototype-builtins
    } else if (!item.group && !obj.hasOwnProperty("nogroup")) {
      obj.nogroup = [];
    }
    // Push the number to its integer key
    // eslint-disable-next-line no-prototype-builtins
    if (item.group && obj.hasOwnProperty(item.group))
      obj[item.group as string].push(item);
    else obj.nogroup.push(item);
    // Pass the object on to the next loop
    return obj;
  }, temp);
  function addToRefs(el: HTMLSpanElement | null) {
    if (
      el &&
      el.parentElement?.parentElement &&
      Array.isArray(navRef.current) &&
      !navRef.current.includes(el)
    ) {
      navRef.current.push(el.parentElement.parentElement);
    }
  }
  useEffect(() => {
    function handleOver(target: HTMLSpanElement) {
      navRef.current.forEach((nav) => {
        nav.classList.remove("not-visited");
        if (target === nav) {
          nav.classList.add(classes.activeTab);
        } else {
          nav.classList.remove(classes.activeTab);
        }
      });
    }
    function handleOut() {
      navRef.current.forEach((nav) => {
        nav.classList.remove(classes.activeTab);
      });
    }
    if (Array.isArray(navRef.current)) {
      navRef.current.forEach((nav) => {
        if (level === 0) nav.classList.add("not-visited");
        nav.addEventListener("mouseover", () => handleOver(nav));
        nav.addEventListener("mouseout", handleOut);
      });
    }
    return () => {
      if (Array.isArray(navRef.current)) {
        navRef.current.forEach((nav) => {
          nav.removeEventListener("mouseover", () => handleOver(nav));
          nav.removeEventListener("mouseout", handleOut);
        });
      }
    };
  }, []);
  function handleMouseEnter(_id: string, level: number) {
    mouseThrottleTimer.current = setTimeout(() => {
      handleChange(_id, level);
      if (mouseThrottleTimer.current) clearTimeout(mouseThrottleTimer.current);
    }, 300);
  }
  function handleMouseLeave() {
    if (mouseThrottleTimer.current) clearTimeout(mouseThrottleTimer.current);
  }
  return (
    <>
      <div data-testid="sideways-megamenu-column" className={classes.column}>
        {Object.entries(groupedData).map(
          (entry: [string, Data[]], index: number) => {
            return (
              <Fragment key={`${entry[0]}`}>
                <Typography
                  key={entry[0]}
                  variant="subtitle2"
                  className={classes.column_header}
                  style={{
                    visibility: entry[0] !== "nogroup" ? "visible" : "hidden",
                    cursor:
                      entry[0] !== "nogroup" && index !== 0
                        ? "pointer"
                        : "default",
                    textAlign: rtl ? "right" : "left",
                  }}
                  align="right"
                  onClick={
                    index !== 0
                      ? () => handleChange(false, level - 1)
                      : () => {}
                  }
                >
                  {entry[0] !== "nogroup" ? t(entry[0]) : "."}
                </Typography>
                <VerticalScroll
                  containerClassName={classes.scrollContainer}
                  contentClassName={classes.contentContainer}
                >
                  <Tabs
                    orientation="vertical"
                    value={
                      entry[1].some((item) => item._id === values[level])
                        ? values[level]
                        : false
                    }
                    aria-label="Vertical megamenu"
                    classes={{
                      root: classes.tabsRoot,
                      indicator: classes.selectedIndicator,
                    }}
                  >
                    {entry[1].map((item) => {
                      if (item.children.length > 0)
                        return (
                          <Tab
                            label={
                              <>
                                <Typography
                                  variant="subtitle2"
                                  ref={addToRefs}
                                  className={classes.navItem}
                                  style={{ textAlign: rtl ? "right" : "left" }}
                                >
                                  {getTitle(item)}
                                </Typography>
                              </>
                            }
                            icon={getIcon(item)}
                            key={item._id}
                            value={item._id}
                            component={Link}
                            to={`/${tenant}${item.link}`}
                            onClick={toggleMenu}
                            disabled={!item.enabled}
                            data-testid="sideways-megamenu-tab"
                            className={clsx(
                              classes.tab,
                              level !== 0 && classes.childNavItem
                            )}
                            onMouseEnter={() =>
                              handleMouseEnter(item._id, level)
                            }
                            onMouseLeave={() => handleMouseLeave()}
                            classes={{
                              selected: classes.selectedTab,
                              root: classes.tabRoot,
                              wrapper: classes.wrapper,
                              labelIcon: classes.labelIcon,
                            }}
                          />
                        );
                      if (RenderTab) {
                        return <RenderTab item={item} onClick={toggleMenu} />;
                      }
                      return checkIfExternal(item.link) ? (
                        <Tab
                          disabled={!item.enabled}
                          key={item._id}
                          data-testid="sideways-megamenu-tab"
                          className={clsx(
                            classes.tab,
                            level !== 0 && classes.childNavItem
                          )}
                          label={
                            <>
                              <Typography
                                variant="subtitle2"
                                ref={addToRefs}
                                className={classes.navItem}
                                style={{ textAlign: rtl ? "right" : "left" }}
                              >
                                {getTitle(item)}
                              </Typography>
                            </>
                          }
                          icon={getIcon(item)}
                          value={item._id}
                          href={item.link}
                          onClick={toggleMenu}
                          target="_blank"
                          classes={{
                            root: classes.tabRoot,
                            wrapper: classes.wrapper,
                            labelIcon: classes.labelIcon,
                          }}
                        />
                      ) : (
                        <Tab
                          disabled={!item.enabled}
                          component={Link}
                          to={`/${tenant}${item.link}`}
                          onClick={toggleMenu}
                          key={item._id}
                          data-testid="sideways-megamenu-tab"
                          className={clsx(
                            classes.tab,
                            level !== 0 && classes.childNavItem
                          )}
                          label={
                            <>
                              <Typography
                                variant="subtitle2"
                                ref={addToRefs}
                                className={classes.navItem}
                                style={{ textAlign: rtl ? "right" : "left" }}
                              >
                                {getTitle(item)}
                              </Typography>
                            </>
                          }
                          icon={getIcon(item)}
                          value={item._id}
                          classes={{
                            root: classes.tabRoot,
                            wrapper: classes.wrapper,
                            labelIcon: classes.labelIcon,
                          }}
                        />
                      );
                    })}
                  </Tabs>
                </VerticalScroll>
                {index + 1 < Object.entries(groupedData).length && <Divider />}
              </Fragment>
            );
          }
        )}
      </div>
      <Divider
        ref={dividerRef}
        orientation="vertical"
        flexItem
        className={classes.divider}
      />
      {data.children
        .filter((item) => values[level] === item._id)
        .map((item) => {
          if (item.children.length > 0)
            return (
              <MenuColumn
                key={item._id}
                data={item}
                level={level + 1}
                tenant={tenant}
                handleChange={handleChange}
                RenderTab={RenderTab}
                values={values}
                toggleMenu={toggleMenu}
              />
            );
          return null;
        })}
    </>
  );
};
export default MenuColumn;
